import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import Portal from '../components/Portal';
import '../components/Modal.css';
import './EmprestimosPage.css';

const fmt = v => (v || 0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

export default function EmprestimosPage() {
  const { user, profile } = useAuth();
  const [emprestimos, setEmprestimos] = useState([]);
  const [pagamentos, setPagamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [selected, setSelected] = useState(null);
  const [msg, setMsg] = useState({ type:'', text:'' });
  const [accessibleIds, setAccessibleIds] = useState([]);

  useEffect(() => {
    if (!profile) return;
    const ids = [profile.id];
    if (profile.shared_with) ids.push(profile.shared_with);
    supabase.from('profiles').select('id').eq('shared_with', profile.id).then(({ data }) => {
      if (data) data.forEach(p => { if (!ids.includes(p.id)) ids.push(p.id); });
      setAccessibleIds(ids);
    });
  }, [profile]);

  const loadData = useCallback(async () => {
    if (!accessibleIds.length) return;
    setLoading(true);
    const [empRes, pagRes] = await Promise.all([
      supabase.from('emprestimos').select('*').in('owner_id', accessibleIds).eq('ativo', true).order('data_inicio'),
      supabase.from('emprestimo_pagamentos').select('*').in('owner_id', accessibleIds).order('ano').order('mes'),
    ]);
    setEmprestimos(empRes.data || []);
    setPagamentos(pagRes.data || []);
    setLoading(false);
  }, [accessibleIds]);

  useEffect(() => { loadData(); }, [loadData]);

  function flash(type, text) {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type:'', text:'' }), 4000);
  }

  async function handleSave(form) {
    const payload = {
      nome: form.nome.trim(),
      tipo: form.tipo,
      valor_total: parseFloat(form.valor_total),
      saldo_atual: parseFloat(form.valor_total),
      parcelas_total: form.tipo === 'parcelado' ? parseInt(form.parcelas_total) : null,
      valor_parcela: form.tipo === 'parcelado' ? parseFloat(form.valor_parcela) : null,
      taxa_juros: parseFloat(form.taxa_juros) || 0,
      data_inicio: form.data_inicio,
      observacao: form.observacao?.trim() || null,
    };
    if (editEmp) {
      await supabase.from('emprestimos').update(payload).eq('id', editEmp.id);
      flash('success', 'Empréstimo atualizado!');
      setSelected({ id: editEmp.id });
    } else {
      // Insert sem select para evitar erro de RLS
      const { error: insertError } = await supabase.from('emprestimos').insert({ ...payload, owner_id: user.id });
      if (insertError) { flash('error', 'Erro ao cadastrar: ' + insertError.message); return; }
      flash('success', 'Empréstimo cadastrado! Clique nele para registrar pagamentos.');
    }
    setShowModal(false);
    setEditEmp(null);
    await loadData();
  }

  async function handleDelete(emp) {
    if (!window.confirm(`Excluir "${emp.nome}"? Todos os pagamentos também serão removidos.`)) return;
    await supabase.from('emprestimos').update({ ativo: false }).eq('id', emp.id);
    flash('success', 'Empréstimo excluído.');
    if (selected?.id === emp.id) setSelected(null);
    await loadData();
  }

  async function handlePagamento(empId, ano, mes, valorPago, saldoDevedor, extras = {}) {
    const existing = pagamentos.find(p => p.emprestimo_id === empId && p.ano === ano && p.mes === mes);
    const payload = {
      emprestimo_id: empId,
      owner_id: user.id,
      ano, mes,
      parcela_num: extras.parcela_num || null,
      valor_pago: parseFloat(valorPago) || 0,
      saldo_devedor: parseFloat(saldoDevedor) || 0,
      ipca_aplicado: parseFloat(extras.ipca_aplicado) || 0,
      correcao_valor: parseFloat(extras.correcao_valor) || 0,
    };
    if (existing) {
      await supabase.from('emprestimo_pagamentos').update(payload).eq('id', existing.id);
    } else {
      await supabase.from('emprestimo_pagamentos').insert(payload);
    }
    // Atualizar saldo_atual do empréstimo
    await supabase.from('emprestimos').update({ saldo_atual: parseFloat(saldoDevedor) }).eq('id', empId);
    await loadData();
  }

  async function handleDeletePagamento(pagId, empId) {
    await supabase.from('emprestimo_pagamentos').delete().eq('id', pagId);
    // Recalcular saldo atual baseado no último pagamento
    const pagsRestantes = pagamentos.filter(p => p.emprestimo_id === empId && p.id !== pagId);
    const ultimo = pagsRestantes.sort((a,b) => b.ano - a.ano || b.mes - a.mes)[0];
    const emp = emprestimos.find(e => e.id === empId);
    const novoSaldo = ultimo?.saldo_devedor ?? emp?.valor_total ?? 0;
    await supabase.from('emprestimos').update({ saldo_atual: novoSaldo }).eq('id', empId);
    await loadData();
  }

  return (
    <div className="emp-page fade-in">
      <div className="emp-header">
        <div>
          <h2 className="emp-title">Empréstimos</h2>
          <p className="text-muted text-sm">Controle de empréstimos e financiamentos</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditEmp(null); setShowModal(true); }}>
          + Novo Empréstimo
        </button>
      </div>

      {msg.text && <div className={`alert alert-${msg.type === 'error' ? 'error' : 'success'}`}>{msg.text}</div>}

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'60px 0', gap:12, color:'var(--text2)' }}>
          <div className="spinner" style={{ width:28, height:28 }} /><span>Carregando...</span>
        </div>
      ) : emprestimos.length === 0 ? (
        <div className="emp-empty">
          <p style={{ fontSize:'2rem' }}>🏦</p>
          <p>Nenhum empréstimo cadastrado.</p>
          <p className="text-muted text-sm">Clique em "+ Novo Empréstimo" para começar.</p>
        </div>
      ) : (
        <div className="emp-layout">
          <div className="emp-lista">
            {emprestimos.map(emp => {
              const pags = pagamentos.filter(p => p.emprestimo_id === emp.id);
              const totalPago = pags.reduce((a, p) => a + p.valor_pago, 0);
              const saldoAtual = emp.saldo_atual ?? emp.valor_total;
              const progresso = ((emp.valor_total - saldoAtual) / emp.valor_total) * 100;
              const isSelected = selected?.id === emp.id;

              return (
                <div key={emp.id} className={`emp-card ${isSelected ? 'selected' : ''}`} onClick={() => setSelected(emp)}>
                  <div className="emp-card-header">
                    <div>
                      <div className="emp-card-nome">{emp.nome}</div>
                      <span className={`emp-tipo-badge ${emp.tipo}`}>
                        {emp.tipo === 'parcelado' ? '📅 Parcelado' : '🔄 Livre + IPCA'}
                      </span>
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="acao-btn edit" onClick={e => { e.stopPropagation(); setEditEmp(emp); setShowModal(true); }}>✎</button>
                      <button className="acao-btn del" onClick={e => { e.stopPropagation(); handleDelete(emp); }}>✕</button>
                    </div>
                  </div>
                  <div className="emp-card-valor">{fmt(emp.valor_total)}</div>
                  <div className="emp-card-info text-xs text-muted">
                    {emp.tipo === 'parcelado'
                      ? `${emp.parcelas_total}x ${fmt(emp.valor_parcela)}${emp.taxa_juros > 0 ? ` · ${emp.taxa_juros}% a.m.` : ''}`
                      : `Pagamento livre · Correção IPCA anual`
                    }
                  </div>
                  <div className="emp-progress-wrap">
                    <div className="emp-progress-bar-bg">
                      <div className="emp-progress-bar-fill" style={{ width:`${Math.min(100, Math.max(0, progresso))}%` }} />
                    </div>
                    <div className="emp-progress-labels text-xs">
                      <span style={{ color:'var(--accent)' }}>Pago: {fmt(totalPago)}</span>
                      <span style={{ color:'var(--danger)' }}>Saldo: {fmt(saldoAtual)}</span>
                    </div>
                  </div>
                  {emp.tipo === 'parcelado' && (
                    <div className="emp-parcelas text-xs text-muted">
                      {pags.length} de {emp.parcelas_total} parcelas registradas
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {selected && (
            <div className="emp-detalhe">
              <div className="emp-detalhe-header">
                <h3 className="emp-detalhe-title">
                  {emprestimos.find(e => e.id === selected.id)?.nome}
                </h3>
                <span className="text-xs text-muted">Clique no mês para registrar</span>
              </div>
              {emprestimos.find(e => e.id === selected.id)?.tipo === 'parcelado' ? (
                <PagamentosParcelado
                  emp={emprestimos.find(e => e.id === selected.id)}
                  pagamentos={pagamentos.filter(p => p.emprestimo_id === selected.id)}
                  onPagamento={handlePagamento}
                  onDeletePagamento={id => handleDeletePagamento(id, selected.id)}
                />
              ) : (
                <PagamentosLivre
                  emp={emprestimos.find(e => e.id === selected.id)}
                  pagamentos={pagamentos.filter(p => p.emprestimo_id === selected.id)}
                  onPagamento={handlePagamento}
                  onDeletePagamento={id => handleDeletePagamento(id, selected.id)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {showModal && (
        <EmprestimoModal emp={editEmp} onSave={handleSave} onClose={() => { setShowModal(false); setEditEmp(null); }} />
      )}
    </div>
  );
}

// ── Grid para empréstimo PARCELADO ──────────────────────────
function PagamentosParcelado({ emp, pagamentos, onPagamento, onDeletePagamento }) {
  const dataInicio = new Date(emp.data_inicio + 'T00:00:00');
  const anoInicio = dataInicio.getFullYear();
  const mesInicio = dataInicio.getMonth();
  const anoAtual = new Date().getFullYear();
  const anos = Array.from({ length: anoAtual - anoInicio + 2 }, (_, i) => anoInicio + i);
  const [anoSel, setAnoSel] = useState(anoAtual);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ valor_pago:'', saldo_devedor:'' });

  function getPag(mes) { return pagamentos.find(p => p.ano === anoSel && p.mes === mes + 1); }
  function getParcelaNum(ano, mes) { return (ano - anoInicio) * 12 + (mes - mesInicio) + 1; }

  function startEdit(mes) {
    const pag = getPag(mes);
    // Calcular saldo anterior
    const pagsAnteriores = pagamentos.filter(p => p.ano < anoSel || (p.ano === anoSel && p.mes < mes + 1));
    const ultimoPagAnterior = pagsAnteriores.sort((a,b) => b.ano - a.ano || b.mes - a.mes)[0];
    const saldoAnterior = ultimoPagAnterior?.saldo_devedor ?? emp.valor_total;
    const valorParcela = parseFloat(emp.valor_parcela) || 0;
    const saldoCalculado = Math.max(0, saldoAnterior - valorParcela);
    setForm({
      valor_pago: pag ? String(pag.valor_pago) : String(emp.valor_parcela || ''),
      saldo_devedor: pag ? String(pag.saldo_devedor) : String(saldoCalculado.toFixed(2)),
    });
    setEditando(mes);
  }

  async function saveEdit(mes) {
    await onPagamento(emp.id, anoSel, mes + 1, form.valor_pago, form.saldo_devedor, {
      parcela_num: getParcelaNum(anoSel, mes)
    });
    setEditando(null);
  }

  return (
    <div className="pag-grid-wrap">
      <div className="pag-ano-tabs">
        {anos.map(a => (
          <button key={a} className={`btn btn-sm ${anoSel === a ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setAnoSel(a)}>{a}</button>
        ))}
      </div>
      <div className="pag-grid">
        {MESES.map((m, i) => {
          const parcelaNum = getParcelaNum(anoSel, i);
          if (parcelaNum < 1 || parcelaNum > emp.parcelas_total) return (
            <div key={i} className="pag-cell disabled">
              <div className="pag-mes">{m}</div>
              <div className="pag-status">—</div>
            </div>
          );
          const pag = getPag(i);
          if (editando === i) return (
            <div key={i} className="pag-cell editing">
              <div className="pag-mes">{m}</div>
              <input type="number" step="0.01" placeholder="Valor pago" value={form.valor_pago}
                onChange={e => {
                  const pagsAnteriores = pagamentos.filter(p => p.ano < anoSel || (p.ano === anoSel && p.mes < editando + 1));
                  const ultimoPagAnterior = pagsAnteriores.sort((a,b) => b.ano - a.ano || b.mes - a.mes)[0];
                  const saldoAnterior = ultimoPagAnterior?.saldo_devedor ?? emp.valor_total;
                  const valorPago = parseFloat(e.target.value) || 0;
                  const novoSaldo = Math.max(0, saldoAnterior - valorPago);
                  setForm(p => ({ ...p, valor_pago: e.target.value, saldo_devedor: novoSaldo.toFixed(2) }));
                }} className="pag-input" autoFocus />
              <input type="number" step="0.01" placeholder="Saldo devedor" value={form.saldo_devedor}
                onChange={e => setForm(p => ({ ...p, saldo_devedor: e.target.value }))} className="pag-input" />
              <div className="text-xs text-muted" style={{ marginTop:2 }}>Saldo calculado automaticamente</div>
              <div style={{ display:'flex', gap:4, marginTop:4 }}>
                <button className="btn btn-primary btn-sm" style={{ flex:1, padding:'4px' }} onClick={() => saveEdit(i)}>✓</button>
                <button className="btn btn-ghost btn-sm" style={{ flex:1, padding:'4px' }} onClick={() => setEditando(null)}>✕</button>
              </div>
            </div>
          );
          return (
            <div key={i} className={`pag-cell ${pag ? 'pago' : 'pendente'}`} onClick={() => startEdit(i)}>
              <div className="pag-mes">{m}</div>
              <div className="pag-parcela text-xs">Parcela {parcelaNum}</div>
              {pag ? (
                <>
                  <div className="pag-valor">{fmt(pag.valor_pago)}</div>
                  <div className="pag-saldo text-xs">Saldo: {fmt(pag.saldo_devedor)}</div>
                  <button className="pag-del" onClick={e => { e.stopPropagation(); onDeletePagamento(pag.id); }}>×</button>
                </>
              ) : (
                <div className="pag-status">+ Registrar</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Grid para empréstimo LIVRE + IPCA ──────────────────────
function PagamentosLivre({ emp, pagamentos, onPagamento, onDeletePagamento }) {
  const dataInicio = new Date(emp.data_inicio + 'T00:00:00');
  const anoInicio = dataInicio.getFullYear();
  const anoAtual = new Date().getFullYear();
  const anos = Array.from({ length: anoAtual - anoInicio + 2 }, (_, i) => anoInicio + i);
  const [anoSel, setAnoSel] = useState(anoAtual);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ valor_pago:'', saldo_devedor:'', ipca_aplicado:'', correcao_valor:'' });

  function getPag(mes) { return pagamentos.find(p => p.ano === anoSel && p.mes === mes + 1); }

  function getSaldoAnterior(mes) {
    // Busca o saldo do mês imediatamente anterior
    const pagsAnteriores = pagamentos
      .filter(p => p.ano < anoSel || (p.ano === anoSel && p.mes < mes + 1))
      .sort((a,b) => b.ano - a.ano || b.mes - a.mes);
    return pagsAnteriores[0]?.saldo_devedor ?? emp.valor_total;
  }

  function startEdit(mes) {
    const pag = getPag(mes);
    const saldoAnterior = getSaldoAnterior(mes);
    setForm({
      valor_pago: pag ? String(pag.valor_pago) : '',
      saldo_devedor: pag ? String(pag.saldo_devedor) : String(saldoAnterior),
      ipca_aplicado: pag ? String(pag.ipca_aplicado || '') : '',
      correcao_valor: pag ? String(pag.correcao_valor || '') : '',
    });
    setEditando(mes);
  }

  async function saveEdit(mes) {
    await onPagamento(emp.id, anoSel, mes + 1, form.valor_pago, form.saldo_devedor, {
      ipca_aplicado: form.ipca_aplicado,
      correcao_valor: form.correcao_valor,
    });
    setEditando(null);
  }

  return (
    <div className="pag-grid-wrap">
      <div className="pag-ano-tabs">
        {anos.map(a => (
          <button key={a} className={`btn btn-sm ${anoSel === a ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setAnoSel(a)}>{a}</button>
        ))}
      </div>

      {/* Aviso IPCA em janeiro */}
      <div className="ipca-aviso">
        💡 Em <strong>Janeiro</strong> de cada ano, informe o IPCA do ano anterior para registrar a correção do saldo.
      </div>

      <div className="pag-grid">
        {MESES.map((m, i) => {
          const pag = getPag(i);
          const isJaneiro = i === 0;

          if (editando === i) return (
            <div key={i} className={`pag-cell editing ${isJaneiro ? 'janeiro' : ''}`}>
              <div className="pag-mes">{m} {isJaneiro && <span className="ipca-tag">IPCA</span>}</div>
              <input type="number" step="0.01" placeholder="Valor pago (R$)" value={form.valor_pago}
                onChange={e => {
                  const valorPago = parseFloat(e.target.value) || 0;
                  const saldoBase = getSaldoAnterior(editando);
                  const novoSaldo = Math.max(0, saldoBase - valorPago);
                  setForm(p => ({ ...p, valor_pago: e.target.value, saldo_devedor: novoSaldo.toFixed(2) }));
                }} className="pag-input" autoFocus />
              <input type="number" step="0.01" placeholder="Saldo devedor (R$)" value={form.saldo_devedor}
                onChange={e => setForm(p => ({ ...p, saldo_devedor: e.target.value }))} className="pag-input" />
              <div className="text-xs text-muted" style={{ marginTop:2 }}>Saldo calculado automaticamente</div>
              {isJaneiro && (
                <>
                  <input type="number" step="0.01" placeholder="IPCA % (ex: 4.83)" value={form.ipca_aplicado}
                    onChange={e => {
                      const ipca = parseFloat(e.target.value) || 0;
                      const saldoBase = getSaldoAnterior(editando);
                      const valorPagoAtual = parseFloat(form.valor_pago) || 0;
                      const saldoAposPagemento = Math.max(0, saldoBase - valorPagoAtual);
                      const correcao = (saldoAposPagemento * ipca) / 100;
                      const saldoComCorrecao = saldoAposPagemento + correcao;
                      setForm(p => ({ ...p, ipca_aplicado: e.target.value, correcao_valor: correcao.toFixed(2), saldo_devedor: saldoComCorrecao.toFixed(2) }));
                    }} className="pag-input" />
                  {form.correcao_valor > 0 && (
                    <div className="ipca-correcao text-xs">
                      Correção: +{fmt(parseFloat(form.correcao_valor))}
                    </div>
                  )}
                </>
              )}
              <div style={{ display:'flex', gap:4, marginTop:4 }}>
                <button className="btn btn-primary btn-sm" style={{ flex:1, padding:'4px' }} onClick={() => saveEdit(i)}>✓</button>
                <button className="btn btn-ghost btn-sm" style={{ flex:1, padding:'4px' }} onClick={() => setEditando(null)}>✕</button>
              </div>
            </div>
          );

          return (
            <div key={i} className={`pag-cell ${pag ? 'pago' : 'pendente'} ${isJaneiro ? 'janeiro' : ''}`} onClick={() => startEdit(i)}>
              <div className="pag-mes">{m} {isJaneiro && <span className="ipca-tag">IPCA</span>}</div>
              {pag ? (
                <>
                  <div className="pag-valor">{fmt(pag.valor_pago)}</div>
                  {pag.ipca_aplicado > 0 && (
                    <div className="text-xs" style={{ color:'var(--warn)' }}>IPCA: {pag.ipca_aplicado}%</div>
                  )}
                  <div className="pag-saldo text-xs">Saldo: {fmt(pag.saldo_devedor)}</div>
                  <button className="pag-del" onClick={e => { e.stopPropagation(); onDeletePagamento(pag.id); }}>×</button>
                </>
              ) : (
                <div className="pag-status">+ Registrar</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Modal de cadastro ────────────────────────────────────────
function EmprestimoModal({ emp, onSave, onClose }) {
  const [form, setForm] = useState({
    nome: emp?.nome || '',
    tipo: emp?.tipo || 'parcelado',
    valor_total: emp?.valor_total || '',
    parcelas_total: emp?.parcelas_total || '',
    valor_parcela: emp?.valor_parcela || '',
    taxa_juros: emp?.taxa_juros || '',
    data_inicio: emp?.data_inicio || new Date().toISOString().slice(0,10),
    observacao: emp?.observacao || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  useEffect(() => {
    if (form.tipo === 'parcelado' && form.valor_total && form.parcelas_total && !form.taxa_juros) {
      const parcela = parseFloat(form.valor_total) / parseInt(form.parcelas_total);
      if (!isNaN(parcela)) set('valor_parcela', parcela.toFixed(2));
    }
  }, [form.valor_total, form.parcelas_total, form.tipo]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.nome.trim()) { setError('Informe o nome.'); return; }
    if (!form.valor_total) { setError('Informe o valor total.'); return; }
    if (form.tipo === 'parcelado' && !form.parcelas_total) { setError('Informe o número de parcelas.'); return; }
    if (form.tipo === 'parcelado' && !form.valor_parcela) { setError('Informe o valor da parcela.'); return; }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  }

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-box fade-in" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">{emp ? 'Editar Empréstimo' : 'Novo Empréstimo'}</h3>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label>Nome / Descrição</label>
              <input type="text" placeholder="Ex: Empréstimo familiar, Financiamento do carro" value={form.nome}
                onChange={e => set('nome', e.target.value)} autoFocus maxLength={80} />
            </div>

            <div className="form-group">
              <label>Tipo de Empréstimo</label>
              <select value={form.tipo} onChange={e => set('tipo', e.target.value)}>
                <option value="parcelado">📅 Parcelado — parcelas fixas e prazo definido</option>
                <option value="livre">🔄 Livre — pagamentos livres</option>
              </select>
            </div>

            <div className="form-group">
              <label>Valor Total (R$)</label>
              <input type="number" step="0.01" placeholder="50000.00" value={form.valor_total}
                onChange={e => set('valor_total', e.target.value)} />
            </div>

            {form.tipo === 'parcelado' && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div className="form-group">
                  <label>Nº de Parcelas</label>
                  <input type="number" placeholder="60" value={form.parcelas_total}
                    onChange={e => set('parcelas_total', e.target.value)} min="1" />
                </div>
                <div className="form-group">
                  <label>Valor da Parcela (R$)</label>
                  <input type="number" step="0.01" placeholder="Auto" value={form.valor_parcela}
                    onChange={e => set('valor_parcela', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Taxa de Juros (% a.m.)</label>
                  <input type="number" step="0.01" placeholder="0.00" value={form.taxa_juros}
                    onChange={e => set('taxa_juros', e.target.value)} />
                </div>
              </div>
            )}

            {form.tipo === 'livre' && (
              <div className="alert" style={{ background:'var(--accent-dim)', border:'1px solid var(--accent)', color:'var(--accent)', fontSize:'0.82rem' }}>
                💡 Pagamentos livres — qualquer valor a qualquer momento. Em janeiro de cada ano, informe o IPCA para corrigir o saldo.
              </div>
            )}

            <div className="form-group">
              <label>Data de Início</label>
              <input type="date" value={form.data_inicio} onChange={e => set('data_inicio', e.target.value)} />
            </div>

            <div className="form-group">
              <label>Observação (opcional)</label>
              <input type="text" placeholder="Ex: Empréstimo do João, contrato nº 123" value={form.observacao}
                onChange={e => set('observacao', e.target.value)} maxLength={200} />
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <><span className="spinner" /> Salvando...</> : emp ? 'Salvar Alterações' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
}
